import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  CONTRACT_CLAUSES,
  ContractClause,
  KEY_ACCOUNT_ESTIMATE_STATUSES,
  KeyAccountEstimateStatus,
  PRODUCT_CATEGORIES,
  ProductCategory,
} from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useQuery } from '@tanstack/react-query'
import { ColumnSort } from '@tanstack/react-table'
import { z } from 'zod'

export const budgetTrackerByKeyAccountSearchSchema = z.object({
  productCategory: z.enum(PRODUCT_CATEGORIES).optional(),
  contractClause: z.enum(CONTRACT_CLAUSES).optional(),
  estimateStatus: z.enum(KEY_ACCOUNT_ESTIMATE_STATUSES).optional(),
  crtPeriod: monthStartString.optional(),
  loadedVersionId: z.number().optional(),
  manualInvestmentIds: z.array(z.number()).optional(),
  ...datatableSearchSchema.shape,
})

export type BudgetTrackerByKeyAccountSearch = z.infer<typeof budgetTrackerByKeyAccountSearchSchema>

export type BudgetTrackerByKeyAccountTotals = Awaited<
  ReturnType<typeof getBudgetTrackerByKeyAccount>
>
export type KeyAccountContract = BudgetTrackerByKeyAccountTotals['results'][number]

async function getBudgetTrackerByKeyAccount(
  search: BudgetTrackerByKeyAccountSearch,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['budget-tracker']['by-key-account'].$get(
    { query: stringifyValues(search) },
    { init: { signal } },
  )
  return await res.json()
}

export function useBudgetTrackerByKeyAccount(queries: BudgetTrackerByKeyAccountSearch) {
  return useQuery({
    queryKey: ['budgetTrackerByKeyAccount', queries],
    queryFn: ({ signal }) => getBudgetTrackerByKeyAccount(queries, signal),
  })
}

/** The filters and sorting the export honours. */
type BudgetTrackerByKeyAccountExportFilters = {
  crtPeriod?: string
  productCategory?: ProductCategory
  contractClause?: ContractClause
  estimateStatus?: KeyAccountEstimateStatus
  manualInvestmentIds?: number[]
  sort?: ColumnSort
}

async function getBudgetTrackerByKeyAccountCsv(
  filters: BudgetTrackerByKeyAccountExportFilters,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams(
    Object.entries(stringifyValues(filters)).flatMap(([key, value]) =>
      value ? [[key, value] as [string, string]] : [],
    ),
  )
  const query = params.size > 0 ? `?${params.toString()}` : ''
  return fetchCsvExport(`/crt/budget-tracker/by-key-account/export${query}`, signal)
}

export function budgetTrackerByKeyAccountExportQueryOptions(
  filters: BudgetTrackerByKeyAccountExportFilters,
) {
  return {
    queryKey: ['budgetTrackerByKeyAccountExport', filters] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getBudgetTrackerByKeyAccountCsv(filters, signal),
  }
}
